import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Rating,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { PieChart, LineChart, BarChart } from "@mui/x-charts";
import styles from "./AdminDashboard.module.css";
import { Helmet } from "react-helmet";
import { apiGet } from "../../../utils/ApiManager";
import { useToastContext } from "../../../Context/ToastContext";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from "react-router-dom";


const fetchDashboardData = process.env.REACT_APP_FETCH_DASHBOARD_DATA;
const getReviews = process.env.REACT_APP_GET_DASHBOARD_REVIEWS;

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    yearlyActiveUsers: 0,
    monthlyActiveUsers: 0,
    weeklySettlements: 0,
    totalDiners: 0,
    totalCommition: 0,
    totalCommitionWithGst: 0,
    pendingSettlements: 0,
    ongoingBookings: 0,
    confirmedBookings: 0,
    totalBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    totalRestaurants: 0,
    deactiveRestaurants: 0,
    totalRevenue: 0,
    totalCommission: 0,
    dailyRevenue: [],
    dailyActiveUsers: [],
    totalReviews: 0,
    topRestaurant: "",
  });
  const { showToast } = useToastContext();
  const [bookingStatusData, setBookingStatusData] = useState([]);
  const [revenueLineData, setRevenueLineData] = useState([]);
  // const [activeUserBarData, setActiveUserBarData] = useState([]);
  const [dailyBookingBarData, setDailyBookingBarData] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewPageNo, setReviewPageNo] = useState(1);
  const [dotIdx, setDotIdx] = useState(1);
  const [stop, setStop] = useState(false);
  const reviewPageSize = 3;
  const [maxPages, setMaxPages] = useState(10);
  const totalBookings = bookingStatusData.reduce((sum, item) => sum + item.value, 0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCardClick = (label) => {
    const routes = {
      "Total Restaurants": "/restaurants",
      "Active Restaurants": "/restaurants",
      "Deactivated Restaurants": "/restaurants",
      "Total Bookings": "/bookings",
      "Completed Bookings": "/bookings",
      "Cancelled Bookings": "/bookings",
      "Total Revenue": "/reports",
      "Total Commission": "/reports",
      "Pending Settlements": "reports/settlements",
      "Total Diners": "/diners",
      "Upcoming Bookings": "/bookings",
      "Total Reviews": "/reviews",
      "Top Restaurant": "/restaurants",
    };
    const path = routes[label];
    if (path) navigate(path);
  };

  const cardData = [
    {
      label: "Total Restaurants",
      value: stats.totalRestaurants,
      color: "#1b5369",
    },
    {
      label: "Active Restaurants",
      value: stats.totalRestaurants - stats.deactiveRestaurants,
      color: "#00796B",
    },
    {
      label: "Deactivated Restaurants",
      value: stats.deactiveRestaurants,
      color: "#D32F2F",
    },
    {
      label: "Total Bookings",
      value: stats.totalBookings,
      color: "#206241",
    },
    {
      label: "Completed Bookings",
      value: stats.completedBookings,
      color: "#207d46",
    },
    {
      label: "Cancelled Bookings",
      value: stats.cancelledBookings,
      color: "#F44336",
    },
    {
      label: "Total Diners",
      value: stats.totalDiners,
      color: "#132642",
    },
    {
      label: "Upcoming Bookings",
      value: stats.confirmedBookings, // change to total reviews
      color: "#154672",
    },

    {
      label: "Top Restaurant",
      value: `${stats.topRestaurant}`,
      color: "#49798a",
    },
    {
      label: "Total Revenue",
      value: `₹${stats.totalRevenue.toFixed(2)}`,
      color: "#008080",
    },
    {
      label: "Total Commission",
      value: `₹${stats.totalCommition.toFixed(2)}`,
      color: "#f9530b",
    },
    {
      label: "Pending Settlements",
      value: stats.pendingSettlements,
      color: "#ffb700",
    },
  ];

  const chunkReviews = (arr, size) =>
    arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

  const reviewChunks = chunkReviews(reviews, 3);

  const fetchReviews = async () => {
    try {
      const response = await apiGet(
        `${getReviews}?pageNo=${reviewPageNo}&pageSize=${reviewPageSize}`
      );
      setDotIdx(reviewPageNo - 1);
      if (response.success) {
        setReviews(response?.data || []);
        if ((response?.data?.length < reviewPageSize) || reviewPageNo === 10) {
          if (reviewPageNo === 1) {
            setStop(true);
          } else {
            if(reviewPageNo === 10){
              setReviewPageNo(1);
              return;
            }
            try {
              const response = await apiGet(`${getReviews}?pageNo=${1}&pageSize=${reviewPageSize}`);
              if (response.success) {
                setReviews(response?.data || []);
              }
            } catch (error) {}
            setDotIdx(0);
            setReviewPageNo(1);
          }
        } else {
          setReviewPageNo(reviewPageNo + 1);
        }
      } else {
        showToast(response?.message || "Error fetching reviews list.", "error");
      }
    } catch (error) {
      showToast(error?.response?.message || error?.message || "Error fetching reviews.", "error");
    }
  };
  const fetchData = async () => {
    try {
      const response = await apiGet(fetchDashboardData);
      if (response.success) {
        const data = response.data;
        setStats(data);
        let mxPages = Math.ceil(data?.totalReviews / 3);
        if (mxPages > 10) {
          setMaxPages(10);
        } else {
          setMaxPages(mxPages);
        }

        setBookingStatusData([
          { label: "Confirmed", value: data.confirmedBookings || 0 },
          { label: "Completed", value: data.completedBookings || 0 },
          { label: "Dining", value: data.ongoingBookings || 0 },
          { label: "Cancelled/Rejected", value: data.cancelledBookings || 0 },
        ]);

        setRevenueLineData(
          data.dailyRevenue?.map((item) => ({
            x: new Date(item.date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            }),
            y: item.revenue,
          }))
        );

        // setActiveUserBarData(
        //   data.dailyActiveUsers?.map((item) => ({
        //     x: new Date(item.date).toLocaleDateString("en-GB", {
        //       day: "2-digit",
        //       month: "short",
        //     }),
        //     y: item.count,
        //   }))
        // );

        setDailyBookingBarData(
          data.dailyBookings?.map((item) => ({
            x: new Date(item.date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            }),
            y: item.count,
          }))
        );
      } else {
        showToast(response?.message || "Something went wrong !", "error");
      }
    } catch (error) {
      showToast(error?.response?.message || error?.message || "Something went wrong !", "error");
    }
  };

  useEffect(() => {
    fetchData();
    fetchReviews();
  }, []);

  useEffect(() => {
    const stopper = setInterval(() => {
      if (stop) {
        clearInterval(stopper);
        return;
      }
      fetchReviews();
    }, 5000);
    return () => clearInterval(stopper);
  }, [stop, reviewPageNo]);

  return (
    <Box className={styles.container}>
      <Helmet>
        <title>Dashboard | DiNEST</title>
        <meta name="description" content="View dashboard on DiNEST" />
      </Helmet>

      <div className={styles.summaryAndChartContainer}>
        {/* Summary Section */}
        <div className={styles.summaryLeft}>
          <div className={styles.summaryCardsContainer}>
            {cardData.map((item, i) => (
              <Card
                key={i}
                elevation={3}
                onClick={() => handleCardClick(item.label)}
                className="card"
                sx={{
                  backgroundColor: item.color,
                  color: "#FFFFFF",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.03)",
                  },
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1">{item.label}</Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%", // or fixed width like "200px"
                    }}
                  >
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Bookings Chart */}
        <div className={styles.chartRight}>
          <Card elevation={3} className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Booking Status
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PieChart
                  series={[
                    {
                      data:
                        totalBookings > 0
                          ? bookingStatusData
                          : [{ id: "no-data", value: 1, label: "No Data" }],
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: totalBookings > 0 ? 2 : 0,
                    },
                  ]}
                  height={280}
                  colors={
                    totalBookings > 0 ? ["#ffc445", "#207d46", "#00bb00", "#D32F2F"] : ["#f0f0f0"]
                  }
                  slotProps={{
                    legend: {
                      direction: isMobile ? "row" : "column",
                      position: {
                        vertical: isMobile ? "bottom" : "bottom",
                        horizontal: isMobile ? "center" : "right",
                      },
                    },
                  }}
                />

                {totalBookings === 0 && (
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: "text.secondary", mt: { xs: 2, lg: 0 } }}
                  >
                    No booking data to display
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Charts */}
      <div className={styles.pieChartWrapper}>
        <Card elevation={3} className={styles.bottomChartCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Daily Bookings (Last 15 Days)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {dailyBookingBarData?.some((data) => data.y > 0) ? (
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: dailyBookingBarData.map((b) => b.x),
                  },
                ]}
                series={[
                  {
                    data: dailyBookingBarData.map((b) => b.y),
                    label: "Bookings",
                  },
                ]}
                height={260}
                colors={["#207d46"]}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 1 }}
              >
                No booking data to display
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card elevation={3} className={styles.bottomChartCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Revenue (Last 15 Days)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {revenueLineData?.some((data) => data.y > 0) ? (
              <LineChart
                xAxis={[{ scaleType: "band", data: revenueLineData.map((r) => r.x) }]}
                series={[
                  {
                    data: revenueLineData.map((r) => r.y),
                    label: "Revenue (₹)",
                    area: true,
                  },
                ]}
                height={260}
                colors={["#207d46"]}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 1 }}
              >
                No revenue data to display
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card elevation={3} className={styles.bottomChartCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Recent Customer Reviews
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {reviews?.length > 0 ? (
              <>
                <Slider
                  dots={false}
                  infinite={false}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={false}
                >
                  {reviewChunks.map((group, index) => (
                    <Box key={index} sx={{ px: 2 }}>
                      {group.map((review, i) => (
                        <Box key={i} sx={{ mb: i !== group.length - 1 ? 2 : 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {review.restaurantId?.restaurantName}
                          </Typography>
                          <Rating
                            value={review.rating}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              fontStyle: "italic",
                            }}
                          >
                          <Tooltip title={review.comment?.trim() ? `"${review.comment}"` : "No comments"}>

                            {review.comment?.trim() ? `"${review.comment}"` : "No comments"}
                          </Tooltip>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Slider>
                {/* Pagination Dots for Reviews */}
              </>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 1 }}
              >
                No reviews to display
              </Typography>
            )}

            <div className={styles.paginationDots}>
              {Array.from({ length: maxPages }, (_, i) => (
                <span
                  key={i}
                  className={i === dotIdx ? `${styles.dot} ${styles.activeDot}` : styles.dot}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default SuperAdminDashboard;
